import { Command as CommandPrimitive } from "cmdk";
import { cn } from "cn";
import type * as React from "react";
import { AnimateChangeInHeight } from "@/components/ui/animate-change-height";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import { BwCloseCircleFill, BwSearchLine } from "@/components/ui/icons";

function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        /* sorted */
        "flex flex-col overflow-hidden",
        "h-full w-full",
        "text-text-dark",
        "bg-bg-light rounded-xl",
        className,
      )}
      {...props}
    />
  );
}

type CommandDialogProps = React.ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
  shouldFilter?: boolean;
  filter?: (value: string, search: string, keywords?: string[]) => number;
  showCloseButton?: boolean;
  showOverlay?: boolean;
  className?: string;
  commandClassName?: string;
};

function CommandDialog({
  title = "Command Palette",
  description = "Search for a command to run...",
  shouldFilter,
  filter,
  children,
  showCloseButton = true,
  showOverlay = true,
  className,
  commandClassName,
  ...props
}: CommandDialogProps) {
  return (
    <Dialog {...props}>
      <DialogHeader className="sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        showCloseButton={showCloseButton}
        showOverlay={showOverlay}
        className={cn("overflow-hidden p-0", className)}
      >
        <AnimateChangeInHeight>
          <Command
            shouldFilter={shouldFilter}
            filter={filter}
            className={cn(
              /* sorted */
              "**:[[cmdk-group-heading]]:text-text-dark-low **:data-[slot=command-input-wrapper]:h-14 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 **:[[cmdk-input]]:h-12 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5",
              commandClassName,
            )}
          >
            {children}
          </Command>
        </AnimateChangeInHeight>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The raw cmdk input, for a surface that draws its own header chrome instead of
 * `CommandInput`'s (which bakes in the search mark and the clear button). Here
 * rather than in the surface so `cmdk` keeps exactly one import site.
 */
const CommandInputPrimitive = CommandPrimitive.Input;

function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div
      data-slot="command-input-wrapper"
      className="group w-full flex h-10 items-center gap-2 border-b border-border-dark-low px-3"
    >
      <BwSearchLine className="size-5 shrink-0 text-text-dark-min" />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          /* sorted */
          "flex",
          "h-10 w-full",
          "py-3",
          "text-sm",
          "bg-transparent rounded-md outline-hidden",
          "placeholder:text-text-dark-low",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
      {props?.value && (
        <BwCloseCircleFill
          data-test="command-input-clear-button"
          className={cn(
            /* sorted */
            "shrink-0",
            "size-4",
            "text-text-dark-min",
            "cursor-pointer",
            "group-hover:flex hidden",
          )}
          onClick={(e) => {
            e.preventDefault();
            props?.onValueChange?.("");
          }}
        />
      )}
    </div>
  );
}

function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        /* sorted */
        "overflow-x-hidden overflow-y-auto",
        "max-h-75",
        "scroll-py-1",
        className,
      )}
      {...props}
    />
  );
}

function CommandEmpty({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="py-6 text-center text-sm"
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        /* sorted */
        "overflow-hidden",
        "p-1",
        "text-foreground",
        "**:[[cmdk-group-heading]]:text-text-dark-low **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-sm",
        className,
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn(
        /* sorted */
        "h-px",
        "-mx-1 my-1",
        "bg-bg-dark-min",
        className,
      )}
      {...props}
    />
  );
}

function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        /* sorted */
        "relative flex items-center gap-2",
        "px-2 py-1.5",
        "text-sm",
        "rounded-lg outline-hidden cursor-pointer select-none",
        "data-[selected=true]:bg-bg-dark-min-hover data-[selected=true]:text-text-dark-max",
        "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        /* sorted */
        "ml-auto",
        "text-text-dark-low text-xs tracking-widest",
        className,
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandInputPrimitive,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
